import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getPlaceholders } from '../services/cardTemplatesService';
import type { PlaceholderInfo } from '../types';
import { PlaceholderContext, type PlaceholderContextValue } from './PlaceholderContext';

/**
 * Provider that loads placeholder metadata once and exposes it to `TemplateText`
 * and related UI. Mount once near the app root; failures leave `placeholders`
 * empty so preview still works with `displayContext` resolution only.
 */
export function PlaceholderProvider({ children }: { children: ReactNode }) {
  const [placeholders, setPlaceholders] = useState<PlaceholderInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const fetchPlaceholders = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await getPlaceholders();
      setPlaceholders(data);
    } catch (e) {
      setLoadError(e instanceof Error ? e : new Error('Failed to load placeholders'));
      setPlaceholders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPlaceholders();
  }, [fetchPlaceholders]);

  const value = useMemo<PlaceholderContextValue>(
    () => ({
      placeholders,
      isLoading,
      loadError,
      refresh: fetchPlaceholders,
    }),
    [placeholders, isLoading, loadError, fetchPlaceholders],
  );

  return (
    <PlaceholderContext.Provider value={value}>
      {children}
    </PlaceholderContext.Provider>
  );
}
